import { BoardUid } from "./base";

export type DiscreteOption = {
  uid: string;
  label: string;
};

/** Generate a unique ID for poll options. Falls back for non-secure contexts (e.g., LAN dev). */
export function generateOptionUid(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export type DiscretePollConfig = {
  type: "discrete";
  options: DiscreteOption[];
};

export type SliderPollConfig = {
  type: "slider";
  min: number;
  max: number;
  minLabel?: string;
  maxLabel?: string;
};

export type PollConfig = DiscretePollConfig | SliderPollConfig;

export function isDiscretePollConfig(
  config: PollConfig,
): config is DiscretePollConfig {
  return config.type === "discrete";
}

export function isSliderPollConfig(
  config: PollConfig,
): config is SliderPollConfig {
  return config.type === "slider";
}

export type PollData = BoardUid & {
  question: string;
  createdDate: Date;
  config: PollConfig;
};

export type SerializedPollData = BoardUid & {
  question: string;
  createdDate: number;
  config: PollConfig;
};

/** Normalize legacy string[] options to DiscreteOption[]. No-op if already migrated. */
export function normalizeDiscreteOptions(options: (string | DiscreteOption)[]): DiscreteOption[] {
  return options.map(opt => typeof opt === "string" ? { uid: opt, label: opt } : opt);
}

function normalizePollConfig(config: unknown): PollConfig | undefined {
  if (!config || typeof config !== "object") return undefined;
  const c = config as Record<string, unknown>;
  if (c.type === "discrete" && Array.isArray(c.options)) {
    return { ...c, type: "discrete", options: normalizeDiscreteOptions(c.options) } as DiscretePollConfig;
  }
  return config as PollConfig;
}

export function pollTransform(val: Record<string, unknown> | null): PollData {
  // react-firebase-hooks calls the transform with `null` when the snapshot has no data
  // (e.g. a pinned item references a pollUid whose entity was never written, or was deleted).
  // Pass null through; consumers already use optional chaining on the resulting `poll`.
  if (val == null) return val as unknown as PollData;
  return {
    ...val,
    createdDate: new Date(val.createdDate as number),
    ...(val.config ? { config: normalizePollConfig(val.config) } : {}),
  } as PollData;
}

export function serializePoll(p: PollData): SerializedPollData {
  return {
    ...p,
    createdDate: p.createdDate.getTime(),
  };
}
